import {
  CurrentSectionContext,
  SectionName,
} from 'components/RedesignJourney/providers/CurrentSectionProvider'
import { useCurrentVariant } from 'hooks/useCurrentVariant'
import { useIsomorphicLayoutEffect } from 'hooks/useIsomorphicLayoutEffect'
import {
  createContext,
  useCallback,
  useContext,
  useState,
  type FC,
  type ReactNode,
} from 'react'

type VariantLoadContextState = {
  isCurrentVariantBottleLoading: boolean
  setSectionIsLoading: (
    section: SectionName,
    assetName: string,
    loaded: boolean
  ) => void
  isSectionLoading: (section: SectionName) => boolean
}

const initialVariantLoadState = {
  isCurrentVariantBottleLoading: true,
  setSectionIsLoading: (
    _section: SectionName,
    _assetName: string,
    _loaded: boolean
  ) => {},
  isSectionLoading: (_section: SectionName) => false,
}

// Bottle loader will appear until all of the current section's assets are loaded
// Asset loading states are set individually via setSectionIsLoading
const initialIsLoadingStates = {
  JourneyStart: {
    bottle: true,
  },
  ForTheEye: {
    bottleImageElements: true,
  },
  ForThePure: {
    bottleTop: true,
    staticTopBg: true,
    staticBackShell: true,
    staticGlass: true,
    staticFrontShell: true,
  },
  ForTheFeeling: {
    spoutImageFrames: true,
    capImageFrames: true,
  },
  ForTheTaste: {
    staticGlass: true,
  },
  ForTheRide: {
    staticGlass: true,
    peekAnimationFrames: true,
    flipAnimationFrames: true,
  },
  ForTheFill: {
    staticBackShell: true,
    staticGlass: true,
    staticFrontShell: true,
  },
  ForTheControl: {
    bottleTop: true,
    staticBackShell: true,
    staticGlass: true,
    staticFrontShell: true,
  },
  ForTheFlow: {
    pourImage: true,
    pourWater: true,
  },
  JourneyEnd: {
    bottle: true,
  },
}

export const VariantLoadContext = createContext<VariantLoadContextState>(
  initialVariantLoadState
)

const VariantLoadProvider: FC<{
  children?: ReactNode
}> = ({ children }) => {
  const { currentVariant } = useCurrentVariant()
  const { currentSection } = useContext(CurrentSectionContext)

  const [isCurrentVariantBottleLoading, setIsCurrentVariantBottleLoading] =
    useState<boolean>(true)

  const [loadingState, setLoadingState] = useState(initialIsLoadingStates)

  const setSectionIsLoading = useCallback(
    (section: SectionName, assetName: string, isLoading: boolean) => {
      setLoadingState((prevLoadingState) => ({
        ...prevLoadingState,
        [section]: {
          ...prevLoadingState[section],
          [assetName]: isLoading,
        },
      }))
    },
    []
  )

  const isSectionLoading = useCallback(
    (section: SectionName) => {
      const sections = loadingState[section]
      const isLoaded = Object.values(sections).every((loading) => !loading)
      return !isLoaded
    },
    [loadingState]
  )

  useIsomorphicLayoutEffect(() => {
    setIsCurrentVariantBottleLoading(true)
    setLoadingState(initialIsLoadingStates)
  }, [currentVariant])

  useIsomorphicLayoutEffect(() => {
    const isLoading = isSectionLoading(currentSection.name)
    if (!isLoading) {
      setIsCurrentVariantBottleLoading(false)
    }
  }, [currentSection.name, isSectionLoading])

  return (
    <VariantLoadContext.Provider
      value={{
        isCurrentVariantBottleLoading,
        setSectionIsLoading,
        isSectionLoading,
      }}
    >
      {children}
    </VariantLoadContext.Provider>
  )
}

export default VariantLoadProvider
